import { Field } from '@/components/Field';
import type { Language } from '@/i18n/dictionary';

import type { QuestionAnswer } from '../screeningAnswers';
import { isChoiceMissing, isFollowUpMissing, openFollowUps } from '../screeningAnswers';
import type { ScreeningQuestion } from '../types';

export interface ScreeningQuestionControlProps {
  readonly question: ScreeningQuestion;
  readonly answer: QuestionAnswer;
  readonly language: Language;
  readonly onChoose: (optionKey: string) => void;
  readonly onFollowUp: (optionKey: string, text: string) => void;
  /** The *answer required* message, set only once the candidate has pressed Continue [AC-8]. */
  readonly requiredMessage?: string | undefined;
}

/** One line, up to 200 characters [D7] — `ScreeningAnswerDto.answer`'s own cap. */
const FOLLOW_UP_MAX_LENGTH = 200;

/**
 * Control characters refused on the way in — every Unicode `Cc`, C0, DEL and C1 (U+0080–U+009F, which
 * includes U+0085 NEXT LINE). A text input already strips line breaks, but a pasted TAB or NEL survives it,
 * and the API refuses a control character in a note pair — which would lose the whole note.
 */
const CONTROL_CHARACTERS = /\p{Cc}/gu;

/**
 * One stored screening control, in the kiosk's current language [03-us-kpw-3a AC-5, AC-6].
 *
 * ⚠️ **No frame draws this** [G1]. `08 · Fit questions` draws three YES/NO placeholders with the
 * `Yes/No Question (radio group)` component [11904:24120]. So this is that component's treatment, and only
 * it, stretched to what the stored set needs — built from the app's own `YesNo` and `Field` (convention
 * skill § 8, *where no frame exists, nothing is invented silently*):
 *
 * - the question line, option buttons, ring, radius, selected tone and validation line are `YesNo`'s,
 *   class for class;
 * - two options sit side by side as `YesNo` does; three or more (Q2's six, Q7b's three) wrap in two
 *   columns so a label is never squeezed to a sliver at 375;
 * - Q9's preamble is the screen subtitle's paragraph style, above the question;
 * - a follow-up is the app's `Field`, under its question.
 *
 * `multi` (Q2) is the same buttons as toggles — `aria-pressed` on each, so a screen reader hears which are
 * on. ⛔ Nothing is exclusive: Q2's `N/A` does not clear the others [KPW-3 § Notes].
 */
export function ScreeningQuestionControl({
  question,
  answer,
  language,
  onChoose,
  onFollowUp,
  requiredMessage,
}: ScreeningQuestionControlProps) {
  const id = `screening-${question.key}`;
  const choiceError = isChoiceMissing(answer) ? requiredMessage : undefined;

  const tone = (chosen: boolean) =>
    chosen
      ? 'ring-[var(--primary)] bg-[var(--primary-muted)] text-[var(--primary-muted-foreground)]'
      : 'ring-[var(--neutral-200)] bg-[var(--card)] text-[var(--muted-foreground)]';

  return (
    <div className="flex w-full flex-col gap-2">
      {question.preamble !== null && (
        <p
          className="w-full text-sm leading-6 text-[var(--muted-foreground)]"
          id={`${id}-preamble`}
        >
          {question.preamble[language]}
        </p>
      )}
      <div
        aria-describedby={question.preamble === null ? undefined : `${id}-preamble`}
        aria-labelledby={`${id}-question`}
        className="flex w-full flex-col gap-2"
        data-screening-question={question.key}
        role="group"
      >
        <div className="flex items-center gap-1">
          <span
            className="text-sm font-bold leading-5 text-[var(--foreground)]"
            id={`${id}-question`}
          >
            {question.prompt[language]}
          </span>
          <span aria-hidden="true" className="text-sm leading-5 text-[var(--destructive)]">
            *
          </span>
        </div>
        <div
          className={
            question.options.length > 2 ? 'grid w-full grid-cols-2 gap-3' : 'flex w-full gap-3'
          }
        >
          {question.options.map((option, index) => (
            <button
              // On the FIRST button, as `YesNo` does, so `useScrollToFirstError` can land on this group.
              aria-invalid={choiceError !== undefined && index === 0 ? true : undefined}
              aria-pressed={answer.chosen.includes(option.key)}
              className={`kiosk-focusable min-h-touch flex-1 rounded-[var(--radius-sm)] px-6 py-3 text-sm font-semibold leading-5 ring-2 ring-inset ${tone(answer.chosen.includes(option.key))}`}
              key={option.key}
              onClick={() => {
                onChoose(option.key);
              }}
              type="button"
            >
              {option.label[language]}
            </button>
          ))}
        </div>
        {choiceError !== undefined && (
          <span className="text-sm leading-5 text-[var(--error)]" role="alert">
            {choiceError}
          </span>
        )}
      </div>
      {openFollowUps(question, answer).map((open) => (
        <Field
          error={isFollowUpMissing(answer, open.optionKey) ? requiredMessage : undefined}
          id={`${id}-${open.optionKey}-follow-up`}
          key={open.optionKey}
          label={open.prompt[language]}
          maxLength={FOLLOW_UP_MAX_LENGTH}
          onChange={(text) => {
            onFollowUp(open.optionKey, text.replace(CONTROL_CHARACTERS, ''));
          }}
          required
          value={answer.followUps[open.optionKey] ?? ''}
        />
      ))}
    </div>
  );
}
